//---------------------------------------------------------------------
// React, MUI, and other third-party modules imports
//---------------------------------------------------------------------
import React, { useContext, useEffect, useState, useCallback } from 'react';
import { useTheme } from '@material-ui/styles';
import { makeStyles, Badge, Modal, useMediaQuery } from '@material-ui/core';
import MessageIcon from '@material-ui/icons/Message';
import CloseIcon from '@material-ui/icons/Close';
import AccessTimeIcon from '@material-ui/icons/AccessTime';
import BusinessIcon from '@material-ui/icons/Business';
import SchoolIcon from '@material-ui/icons/School';
import ArrowBackIosSharpIcon from '@material-ui/icons/ArrowBackIosSharp';
import ArrowForwardIosIcon from '@material-ui/icons/ArrowForwardIos';
import Fab from '@material-ui/core/Fab';
import CircularProgress from '@material-ui/core/CircularProgress';
import LaunchIcon from '@material-ui/icons/Launch';
import VerifiedUserRoundedIcon from '@material-ui/icons/VerifiedUserRounded';
import { mdiSchoolOutline } from '@mdi/js';

//---------------------------------------------------------------------
// Utilities (clsx, lodash, date-fns, etc.) imports
//---------------------------------------------------------------------
import cx from 'clsx';
import { format } from 'date-fns';
import _get from 'lodash/get';
import _isEmpty from 'lodash/isEmpty';

//---------------------------------------------------------------------
// Common helpers and constants imports
//---------------------------------------------------------------------
import { AppContext, LayoutContext } from '../../common/contexts';
import { setClassNameInDefns } from '../../common/definitionHelper';
import { getCityStateCountryText, getJobYearsOfExperienceText, getSalaryRateText } from '../../common/textHelper';
import { getHttpsUrl } from '../../common/linkHelper';
import { getUserDateFormat } from '../../common/dateHelper';
import { fetchJob as fetchJobForJsMyJobs } from '../../common/services/myJobsServices';
import { getBrandPageUrlPathByEmployerId } from '../../common/linkHelper';

//---------------------------------------------------------------------
// Page specific helpers and constants imports
//---------------------------------------------------------------------
import { fetchJob } from '../../common/services/empMyJobsServices';

//---------------------------------------------------------------------
// Common components imports
//---------------------------------------------------------------------
import AfitdMdiIcon from '../../common/elemental/AfitdMdiIcon';
import AfitdTypography from '../../common/elemental/AfitdTypography';
// import AfitdCompanyName from '../../common/elemental/AfitdCompanyName';
import AfitdButton from '../../common/elemental/AfitdButton';
import AfitdLink from '../../common/elemental/AfitdLink';

//---------------------------------------------------------------------
// Page specific components imports
//---------------------------------------------------------------------
import MyTabs from './Tabs';

//---------------------------------------------------------------------
// CSS
//---------------------------------------------------------------------
const useStyles = (params = {}) => {
    const { active } = params;

    return makeStyles(theme => ({
        jobDetailContainer: {
            padding: '10px',
            backgroundColor: '#fff',
            overflowY: 'scroll',
            borderRadius: '10px',
            '&::-webkit-scrollbar': { width: '0.4em' },
            '&::-webkit-scrollbar-track': { '-webkit-box-shadow': 'inset 0 0 6px rgba(0,0,0,0.00)' },
            '&::-webkit-scrollbar-thumb': { backgroundColor: 'rgba(0,0,0,.1)', borderRadius: '5px' },
            [theme.breakpoints.down('sm')]: {
                maxHeight: '98vh',
                overflowY: 'hidden',
            },
        },
        jobContainer: {
            display: 'flex',
            flexDirection: 'column',
            padding: '10px',
            width: 680,
            [theme.breakpoints.down('md')]: { width: '600px' },
            [theme.breakpoints.down('sm')]: { width: '340px' },
        },
        loadingContainer: { height: '250px', display: 'flex', justifyContent: 'center' },
        circularProgressContainer: { lineHeight: '300px' },
        paper: {
            outline: 'none',
            display: 'flex',
            alignItems: 'center',
            [theme.breakpoints.down('lg')]: { marginTop: 30 },
        },

        // arrow: { backgroundColor: '#fff', margin: 4, height: 40, width: 40, borderRadius: 50, display: 'flex', justifyContent: 'center', alignItems: 'center', cursor: 'pointer' },
        leftArrow: { marginRight: '10px' },
        rightArrow: { marginLeft: '10px' },
        // arrowIcons: { fontSize: 16, fontWeight: 'bold' },
        modal: { outline: 'none', display: 'flex', justifyContent: 'center', alignItems: 'center', margin: '0px auto' },

        jobTitleText: { fontWeight: 'bold', flex: 1, paddingRight: 20 },
        reqNumText: { fontStyle: 'italic' },
        statusText: { color: '#1D8A8B', textTransform: 'uppercase', fontWeight: 'bold', marginRight: 15 },
        subInfo: { color: '#1D8A8B', textTransform: 'uppercase', fontWeight: 'bold', marginRight: 15 },
        companyContainer: { padding: '10px 0', display: 'flex' },
        companyNameText: { textDecoration: 'underline' },
        verifiedBadgeIcon: active ? { color: '#1D8A8B', margin: '0 5px' } : { color: '#1D8A8B', opacity: 0.6 },
        // locationContainer: { padding: '10px 0', display: 'flex' },
        detailContainer: { flexDirection: 'row', display: 'flex', alignItems: 'center', marginRight: 20 },
        jobLocationText: { marginBottom: 10 },
        detail: { fontWeight: 'bold' },
        icons: { color: '#9B9E9C', fontSize: 16, marginRight: 10 },
        messageIcon: { color: '#9B9E9C', fontSize: 20 },
        subInfoContainer: { display: 'flex', alignItems: 'center', justifyContent: 'center' },
        miscInfoContainer: { display: 'flex', justifyContent: 'space-between' },
        miscInfo: { display: 'flex', flex: 1, flexWrap: 'wrap' },
        descriptionText: { margin: '15px 125px 0 0' },
        headingSection: {
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'space-between',
            [theme.breakpoints.down('sm')]: { flexDirection: 'column-reverse', alignItems: 'flex-start', justifyContent: 'flex-start' },
        },

        crossContainer: { display: 'flex', justifyContent: 'flex-end' },
        cross: { cursor: 'pointer' },

        applyButtonBox: {
            display: 'flex',
            justifyContent: 'space-between',
            marginTop: 20,
            marginBottom: 20,
            [theme.breakpoints.down('sm')]: { flexDirection: 'column' },
        },
        applyNowButton: {
            [theme.breakpoints.down('sm')]: { marginBottom: 16 },
        },
        inactiveStatus: { color: 'red' },
        hidden: { display: 'none' },
    }));
};

//---------------------------------------------------------------------
// Get definitions
//---------------------------------------------------------------------
const getDefns = params => {
    const {
        classes,
        props,
        userDateFormat,
        currentEmployer,
        currentUser,
        handleChangeIdx,
        currentIdx,
        jobItem,
        employerData,
        handleClickApplyNow,
        handleClickCompanyLogo,
        mdMatchesUp,
    } = params;
    const { close, open, currentJobs = [], isJobSeeker, employers, isAppliedView } = props;
    const { loadingContainer: loadingContainerCls, inactiveStatus: inactiveStatusCls, hidden: hiddenCls } = classes;

    const jobId = _get(jobItem, 'uid');
    const reqNum = _get(jobItem, 'reqNum');
    const updateDt = _get(jobItem, 'updateDt');
    const arrowDisabled = currentJobs.length === 1;
    const active = _get(jobItem, 'active');
    const statusDt = _get(jobItem, 'statusDt');
    // const createDt = _get(jobItem, 'createDt');
    const jobEmployerId = _get(jobItem, 'employerId');
    const country = _get(jobItem, 'country');
    const state = _get(jobItem, 'state');
    const city = _get(jobItem, 'city');

    const reqNumText = reqNum ? `ID#: ${reqNum}` : null;

    const companyName = getJobCompanyName(isJobSeeker, employers, jobEmployerId, currentEmployer);
    const jobLocationText = getCityStateCountryText(city, state, country);
    const jobApplicationUrl = _get(jobItem, 'jobApplicationUrl');

    const comingSoon = _get(jobItem, 'comingSoon');
    const filled = _get(jobItem, 'filled');

    let applyButtonsIsHidden = true;
    // let jobDateLabel = active ? 'created on' : 'since';
    // let jobDate = createDt;
    let jobDateLabel = active ? 'Updated on' : 'Since';
    let jobDate = active ? updateDt : statusDt;
    let applied = false;
    const isUpdatedAfterActive = updateDt > statusDt;
    const isSavedJob = !isAppliedView;

    // jobDateLabel = isUpdatedAfterActive ? 'Updated on' : 'Posted';

    if (isJobSeeker) {
        applyButtonsIsHidden = false;
        jobDateLabel = !isSavedJob ? 'Applied' : !active ? 'Since' : isUpdatedAfterActive ? 'Updated on' : 'Posted';
        jobDate = statusDt;

        const appliedJobs = _get(currentUser, 'appliedJobs');
        applied = !!_get(appliedJobs, jobId);
    }

    const defns = {
        loadingContainer: { className: cx(loadingContainerCls, { [hiddenCls]: !_isEmpty(jobItem) }) },
        circularProgress: { size: 100 },
        jobContentContainer: { className: cx({ [hiddenCls]: _isEmpty(jobItem) }) },
        jobTitleText: { variant: 'h6' },
        reqNumText: { variant: 'body2', value: reqNumText },
        internshipIcon: {
            // color: 'watermark',
            style: { color: '#9B9E9C', width: 18, marginRight: 10 },
            path: mdiSchoolOutline,
        },
        statusText: { variant: 'caption', classNameMixin: cx({ [inactiveStatusCls]: !active }), value: active ? 'Active' : 'Inactive' },
        subInfo: { variant: 'caption' },
        jobDate: { classes, label: jobDateLabel, date: jobDate, dateFormat: userDateFormat },
        companyNameLink: {
            color: 'secondary',
            title: "Visit Company's Brand Page",
            onClick: mdMatchesUp ? undefined : handleClickCompanyLogo,
            href: mdMatchesUp ? getBrandPageUrlPathByEmployerId(jobEmployerId) : undefined,
            target: mdMatchesUp ? '_blank' : undefined,
        },
        companyNameText: {
            variant: 'body1',
            color: 'secondary',
            // clsLightBold: true,
            value: companyName,
            // classNameMixin: cx({ [inactiveCls]: !active }),
        },
        jobLocationText: { variant: 'body1', value: jobLocationText },
        detail: { variant: 'body2' },
        badge: { color: 'secondary', overlap: 'circle', variant: 'dot' },
        descriptionText: { variant: 'body2' },

        modal: { open, onClose: close, /* maxWidth: 'md',*/ disableBackdropClick: true, disableEscapeKeyDown: true },
        cross: { onClick: close },
        leftArrow: { onClick: () => handleChangeIdx(currentIdx - 1), size: 'medium', disabled: arrowDisabled },
        rightArrow: { onClick: () => handleChangeIdx(currentIdx + 1), size: 'medium', disabled: arrowDisabled },

        applyButtons: { classes, isHidden: applyButtonsIsHidden, jobActive: active, applied, applyNowOnClick: handleClickApplyNow, jobApplicationUrl, comingSoon, filled },
        myTabs: { ...jobItem, ...employerData },
    };

    setClassNameInDefns(defns, classes);
    return defns;
};

//---------------------------------------------------------------------
// Helper functions
//---------------------------------------------------------------------
const JobDate = props => {
    const { classes, label, date, dateFormat } = props;
    const defns = { subInfo: { variant: 'caption' } };
    setClassNameInDefns(defns, classes);
    return (
        <AfitdTypography {...defns.subInfo}>
            <i>{label}</i>: {date && format(date, dateFormat)}
        </AfitdTypography>
    );
};

const ApplyButtons = props => {
    const { classes, isHidden, jobActive, applied, applyNowOnClick, jobApplicationUrl, comingSoon, filled } = props;
    const defns = {
        comingSoonButton: {
            color: 'primary',
            disabled: true,
            label: 'Coming Soon',
            variant: 'contained',
            disableElevation: true,
        },
        filledButton: {
            color: 'primary',
            disabled: true,
            label: 'Already Filled',
            variant: 'contained',
            disableElevation: true,
        },
        applyNowButton: {
            color: 'primary',
            disabled: !jobActive || applied,
            label: applied ? 'Applied' : 'Apply Now',
            onClick: () => {
                if (!applied) applyNowOnClick();
            },
            variant: 'contained',
            disableElevation: true,
        },
        applyAtCompanySiteButton: {
            color: 'secondary',
            label: 'Apply on Company’s Site',
            isHidden: !!!jobApplicationUrl,
            disabled: !jobActive,
            onClick: () => {
                window.open(getHttpsUrl(jobApplicationUrl), '_blank');
            },
            variant: 'contained',
            endIcon: <LaunchIcon style={{ fontSize: '24px' }} />,
            disableElevation: true,
        },
    };
    setClassNameInDefns(defns, classes);

    return isHidden ? null : comingSoon ? (
        <div {...defns.applyButtonBox}>
            <AfitdButton {...defns.comingSoonButton} />
        </div>
    ) : filled ? (
        <div {...defns.applyButtonBox}>
            <AfitdButton {...defns.filledButton} />
        </div>
    ) : (
        <div {...defns.applyButtonBox}>
            <AfitdButton {...defns.applyNowButton} />
            <AfitdButton {...defns.applyAtCompanySiteButton} />
        </div>
    );
};

const getJobCompanyName = (isJobSeeker, jobEmployers, jobEmployerId, currentEmployer) => {
    if (isJobSeeker) {
        const jobEmployer = _get(jobEmployers, jobEmployerId);
        return _get(jobEmployer, 'companyName');
    } else {
        return _get(currentEmployer, 'companyName');
    }
};

//---------------------------------------------------------------------
// Component
//---------------------------------------------------------------------
const SingleJobDialog = props => {
    //---------------------------------------------------------------------
    // From context and other hooks
    //---------------------------------------------------------------------
    const appContext = useContext(AppContext);
    const context = useContext(LayoutContext);
    const { currentEmployer, userId, currentUser } = appContext;
    const { profileData, setBrandPageDialogInfo, setSnackbarErrorMessage } = context;
    const userDateFormat = getUserDateFormat(appContext);

    const theme = useTheme();
    // const smMatches = useMediaQuery(theme.breakpoints.down('sm'));
    const mdMatchesUp = useMediaQuery(theme.breakpoints.up('md'));

    const employerTier = _get(currentEmployer, 'tier');

    //---------------------------------------------------------------------
    // From props
    //---------------------------------------------------------------------
    const { open, currentJobs, idx, applyNowOnClick, isJobSeeker, isAppliedView, employers } = props;

    //---------------------------------------------------------------------
    // Local states
    //---------------------------------------------------------------------
    const [jobItem, setJobItem] = useState({});
    const [employerData, setEmployerData] = useState({});
    const [currentIdx, setCurrentIdx] = useState(idx);

    //---------------------------------------------------------------------
    // derived
    //---------------------------------------------------------------------
    const jobId = _get(currentJobs, `[${currentIdx}].uid`);
    const {
        intern,
        jobTitle,
        yrsOfExp,
        active,
        currency,
        currencySymbol,
        // createDt,
        employerId: jobEmployerId,
        // salary,
        // otherSalary,
        isExactRate,
        rateMin,
        rateMax,
        // reqNum,
        employmentType,
        telework,
        education,
        conversations,
        payFrequency,
        otherEducation,
        // comingSoon,
        // filled,
        // rate,
    } = jobItem;
    const employerDataProp = _get(employers, jobEmployerId);
    const isEmpBadge = _get(employerDataProp, `isEmpBadge`);
    const empBadgeVisible = (isJobSeeker && isEmpBadge) || (!isJobSeeker && employerTier === 3);
    const yrsOfExpText = getJobYearsOfExperienceText(yrsOfExp);

    //---------------------------------------------------------------------
    // Side effects
    //---------------------------------------------------------------------
    useEffect(() => {
        if (open) {
            if (isJobSeeker) {
                fetchJobForJsMyJobs({ userId, jobId, isAppliedView, setData: setJobItem, setSnackbarErrorMessage });
            } else {
                fetchJob(jobId, setJobItem, setSnackbarErrorMessage);
            }
        } else {
            setJobItem({});
        }
    }, [open, isJobSeeker, jobId, isAppliedView, userId, setSnackbarErrorMessage]);

    //TODO 'employerData' might not need to be a state because it can be derived -el 20200119
    useEffect(() => {
        if (isJobSeeker) {
            setEmployerData({ ...employerDataProp });
        } else {
            // inactive job data does not contain employer data, therefore, populate it from profileData
            if (active) {
                setEmployerData({});
            } else if (profileData !== null) {
                const benefitSummary = _get(profileData, 'info.benefit', '');
                const companyBio = _get(profileData, 'info.bio', '');
                setEmployerData({ benefitSummary, companyBio });
            }
        }
    }, [isJobSeeker, employerDataProp, active, profileData]);

    //---------------------------------------------------------------------
    // Event handlers
    //---------------------------------------------------------------------
    const handleChangeIdx = val => {
        setJobItem({});
        const jobsLength = currentJobs.length;
        if (val > jobsLength - 1) {
            setCurrentIdx(0);
        } else if (val < 0) {
            setCurrentIdx(jobsLength - 1);
        } else {
            setCurrentIdx(val);
        }
    };

    const handleClickApplyNow = useCallback(() => {
        if (applyNowOnClick) {
            const jobId = _get(jobItem, 'uid');
            const jobEmployerId = _get(jobItem, 'employerId');
            applyNowOnClick(jobId, jobEmployerId);
        }
    }, [jobItem, applyNowOnClick]);

    const handleClickCompanyLogo = evt => {
        evt.stopPropagation();
        setBrandPageDialogInfo({ open: true, employerId: jobEmployerId });
    };

    //---------------------------------------------------------------------
    // Definitions
    //---------------------------------------------------------------------
    const classes = useStyles({ active })();
    const defns = getDefns({
        classes,
        props,
        userDateFormat,
        currentEmployer,
        currentUser,
        handleChangeIdx,
        currentIdx,
        jobItem,
        employerData,
        handleClickApplyNow,
        handleClickCompanyLogo,
        mdMatchesUp,
    });

    // const Duration = { Yearly: 'Year', Monthly: 'Month', Daily: 'Day', Weekly: 'Week', Hourly: 'hour' };

    //---------------------------------------------------------------------
    // Render
    //---------------------------------------------------------------------
    return (
        <Modal {...defns.modal}>
            <div {...defns.paper}>
                <Fab {...defns.leftArrow}>
                    <ArrowBackIosSharpIcon />
                </Fab>
                <div {...defns.jobDetailContainer}>
                    <div {...defns.crossContainer}>
                        <CloseIcon {...defns.cross} />
                    </div>
                    <div {...defns.jobContainer}>
                        <div {...defns.loadingContainer}>
                            <div {...defns.circularProgressContainer}>
                                <CircularProgress {...defns.circularProgress} />
                            </div>
                        </div>
                        <div {...defns.jobContentContainer}>
                            <div {...defns.headingSection}>
                                <div {...defns.jobTitleReqNumContainer}>
                                    <AfitdTypography {...defns.jobTitleText}>{jobTitle}</AfitdTypography>
                                    <div {...defns.reqNumContainer}>
                                        <AfitdTypography {...defns.reqNumText} />
                                    </div>
                                </div>
                                <div>
                                    <AfitdTypography {...defns.statusText} />
                                    <JobDate {...defns.jobDate} />
                                </div>
                            </div>
                            <div {...defns.companyContainer}>
                                <AfitdLink {...defns.companyNameLink}>
                                    <AfitdTypography {...defns.companyNameText} />
                                </AfitdLink>
                                {empBadgeVisible && <VerifiedUserRoundedIcon {...defns.verifiedBadgeIcon} />}
                            </div>
                            {/* <AfitdCompanyName {...defns.companyName} /> */}
                            <AfitdTypography {...defns.jobLocationText} />
                            <div {...defns.miscInfoContainer}>
                                <div {...defns.miscInfo}>
                                    <div {...defns.detailContainer}>
                                        <AfitdTypography {...defns.icons}>{currencySymbol}</AfitdTypography>
                                        <AfitdTypography {...defns.detail}>{getSalaryRateText(isExactRate, rateMin, rateMax, currency, payFrequency)}</AfitdTypography>
                                    </div>
                                    <div {...defns.detailContainer}>
                                        <AccessTimeIcon {...defns.icons} />
                                        <AfitdTypography {...defns.detail}>{yrsOfExpText}</AfitdTypography>
                                    </div>
                                    <div {...defns.detailContainer}>
                                        <AccessTimeIcon {...defns.icons} />
                                        <AfitdTypography {...defns.detail}>{employmentType}</AfitdTypography>
                                    </div>
                                    <div {...defns.detailContainer}>
                                        <BusinessIcon {...defns.icons} />
                                        <AfitdTypography {...defns.detail}>{telework}</AfitdTypography>
                                    </div>
                                    {intern ? (
                                        <div {...defns.detailContainer}>
                                            <AfitdMdiIcon {...defns.internshipIcon} />
                                            <AfitdTypography {...defns.detail}>Intern Position</AfitdTypography>
                                        </div>
                                    ) : null}
                                    <div {...defns.detailContainer}>
                                        <SchoolIcon {...defns.icons} />
                                        <AfitdTypography {...defns.detail}>{education}</AfitdTypography>
                                    </div>
                                    {otherEducation?.map(el => (
                                        <div {...defns.detailContainer}>
                                            <SchoolIcon {...defns.icons} />
                                            <AfitdTypography {...defns.detail}>{el}</AfitdTypography>
                                        </div>
                                    ))}
                                </div>

                                {conversations?.filter(c => c.unread).length > 0 && (
                                    <Badge {...defns.badge}>
                                        <MessageIcon {...defns.messageIcon} />
                                    </Badge>
                                )}
                            </div>

                            <ApplyButtons {...defns.applyButtons} />
                            <MyTabs {...defns.myTabs} />
                        </div>
                    </div>
                </div>
                <Fab {...defns.rightArrow}>
                    <ArrowForwardIosIcon />
                </Fab>
            </div>
        </Modal>
    );
};

export default SingleJobDialog;
